import { useEffect, useState } from "react";
import { Card, ErrorBanner, Pill, fmt } from "../components/ui";
import { api } from "../lib/api";

type Row = {
  cohort: string;
  people: number;
  sent: number;
  delivered: number;
  failed: number;
  repliers: number;
  opted_out: number;
  reply_rate: number;
  opt_out_rate: number;
};

type Dim = { key: string; label: string; at_send: boolean };

const WINDOWS = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "a year" },
];

export function Cohorts() {
  const [by, setBy] = useState("score_band");
  const [days, setDays] = useState(90);
  const [rows, setRows] = useState<Row[]>([]);
  const [dims, setDims] = useState<Dim[]>([]);
  const [atSend, setAtSend] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBusy(true);
    api
      .cohorts(by, days)
      .then((d) => {
        setRows(d.rows);
        setDims(d.dimensions);
        setAtSend(d.at_send);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  }, [by, days]);

  // The best and worst are what the eye should land on, so they are marked
  // rather than left for someone to scan a column and work out.
  const rates = rows.filter((r) => r.people >= 5).map((r) => r.reply_rate);
  const best = rates.length ? Math.max(...rates) : null;
  const worst = rates.length ? Math.min(...rates) : null;

  return (
    <>
      <ErrorBanner error={error} />
      <h2 style={{ fontSize: 19 }}>Cohorts</h2>
      <p className="dim" style={{ marginTop: -4, fontSize: 13, maxWidth: 780 }}>
        The same numbers, cut different ways, so groups can be compared against each
        other rather than against an average that hides them.
      </p>

      <Card title="Compare by">
        <div className="row w" style={{ gap: 6 }}>
          {dims.map((d) => (
            <button
              key={d.key}
              className={`btn s${by === d.key ? " p" : ""}`}
              onClick={() => setBy(d.key)}
            >
              {d.label}
            </button>
          ))}
          <span className="sp" />
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              className={`btn s${days === w.days ? " p" : ""}`}
              onClick={() => setDays(w.days)}
            >
              {w.label}
            </button>
          ))}
        </div>

        {atSend && (
          <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
            This cut uses what was true <b>when the message was sent</b>, not what is
            true now. That matters: grouping by someone's lead score today would show
            that high scorers reply more, which is circular — replying is what raised
            their score. Grouping by their score at send time answers whether messaging
            them was worth it.
          </div>
        )}
      </Card>

      <Card title={busy ? "Working…" : `${rows.length} cohorts`}>
        {rows.length === 0 ? (
          <div className="empty">
            Nothing sent in this window yet, so there is nothing to compare.
          </div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Cohort</th>
                  <th>People</th>
                  <th>Messages</th>
                  <th>Replied</th>
                  <th>Reply rate</th>
                  <th>Opted out</th>
                  <th>Opt-out rate</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const thin = r.people < 5;
                  return (
                    <tr key={r.cohort}>
                      <td>
                        <b>{r.cohort}</b>
                      </td>
                      <td className="mono">{fmt.n(r.people)}</td>
                      <td className="mono dim">{fmt.n(r.sent)}</td>
                      <td className="mono">{fmt.n(r.repliers)}</td>
                      <td className="mono">
                        <b>{r.reply_rate}%</b>
                      </td>
                      <td className="mono dim">{fmt.n(r.opted_out)}</td>
                      <td className="mono">
                        <span style={{ color: r.opt_out_rate > 2 ? "var(--bad)" : undefined }}>
                          {r.opt_out_rate}%
                        </span>
                      </td>
                      <td>
                        {thin ? (
                          <span className="dim" style={{ fontSize: 11 }}>
                            too few to read
                          </span>
                        ) : r.reply_rate === best && best !== worst ? (
                          <Pill tone="ok">best</Pill>
                        ) : r.reply_rate === worst && best !== worst ? (
                          <Pill tone="ba">worst</Pill>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Reading this honestly">
        <div className="col" style={{ gap: 8, fontSize: 12.5 }}>
          <div>
            <b>Cohorts under five people are marked</b> —{" "}
            <span className="dim">
              one reply out of three is 33%, and it means nothing. The label is there so
              nobody builds a plan on it.
            </span>
          </div>
          <div>
            <b>Reply rate counts people, not messages</b> —{" "}
            <span className="dim">
              someone who replies twice is one reply. Counting messages would flatter
              cadences that send more.
            </span>
          </div>
          <div>
            <b>Opt-out rate is the one to watch</b> —{" "}
            <span className="dim">
              above 2% is marked red. A cohort with a good reply rate and a bad opt-out
              rate is burning the list to make its numbers.
            </span>
          </div>
        </div>
      </Card>
    </>
  );
}
